import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, ShieldCheck } from 'lucide-react';
import { posRestaurant } from '../../api/posAPI';
import usePosStore from '../../stores/posStore';

const ACTION_LABELS = {
  move_table:  'Moving this table requires manager or cashier authorization.',
  split_bill:  'Splitting this bill requires manager or cashier authorization.',
  bill_out:    'Billing out requires manager or cashier authorization.',
  void_items:  'Cancelling items requires manager or cashier authorization.',
};

/**
 * ManagerApprovalModal
 * Props:
 *   isOpen, action ('move_table'|'split_bill'|'bill_out'), onApprove(staffId), onClose
 */
export default function ManagerApprovalModal({ isOpen, action, onApprove, onClose }) {
  const { posToken } = usePosStore();
  const [selectedId, setSelectedId] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const staffQuery = useQuery({
    queryKey: ['pos-approvers', posToken],
    queryFn: async () => {
      const res = await posRestaurant.staff(posToken, { role: 'cashier,manager' });
      return res.data.staff || [];
    },
    enabled: !!posToken && isOpen,
    staleTime: 60_000,
  });

  const approvers = staffQuery.data || [];

  const handleApprove = async () => {
    setError(null);
    if (!selectedId || !pin) {
      setError('Select a staff member and enter their PIN');
      return;
    }
    setLoading(true);
    try {
      const res = await posRestaurant.verifyPin(posToken, { staffId: selectedId, pin });
      const { verified, role } = res.data;
      if (!verified) throw new Error('Invalid PIN');
      if (!['cashier', 'manager'].includes(role)) {
        throw new Error('Only cashier or manager can approve this action');
      }
      onApprove(selectedId);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedId('');
    setPin('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
            Manager Approval
          </h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          {ACTION_LABELS[action] || 'This action requires authorization.'}
        </p>

        {error && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mb-3">
          <label className="block text-xs font-semibold text-gray-700 mb-1">Authorized By</label>
          {staffQuery.isLoading ? (
            <p className="text-xs text-gray-400">Loading staff…</p>
          ) : (
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              <option value="">— select —</option>
              {approvers.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name} ({s.role})
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">PIN</label>
          <input
            type="password"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            maxLength={8}
            autoFocus
          />
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApprove}
            disabled={!selectedId || !pin || loading}
            className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            {loading ? 'Verifying…' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}
